<script setup lang="ts">
  import { DropdownMenu, Icon, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, nextTick, ref } from 'vue';
  import type { SeoDeparture, SeoDepartureCity } from '../model/types';

  interface SeoDeparturePickerProps {
    departure: SeoDeparture;
  }
  const props = defineProps<SeoDeparturePickerProps>();

  interface SeoDeparturePickerEmits {
    (e: 'select', city: SeoDepartureCity): void;
  }
  const emit = defineEmits<SeoDeparturePickerEmits>();

  const opened = ref(false);
  const query = ref('');
  const searchInput = ref<InstanceType<typeof Input> | null>(null);

  const options = computed(() => {
    const normalizedQuery = query.value.trim().toLowerCase();

    return props.departure.cities
      .filter((city) => city.name.toLowerCase().includes(normalizedQuery))
      .map((city) => ({ value: city.id, label: city.name, selected: city.id === props.departure.city?.id }));
  });

  const onVisible = async (visible: boolean): Promise<void> => {
    opened.value = visible;
    if (!visible) {
      query.value = '';
      return;
    }
    // Список открыли, чтобы найти город, — сразу печатаем в поиск.
    await nextTick();
    searchInput.value?.focus();
  };

  const onSelect = (id: unknown): void => {
    const city = props.departure.cities.find((item) => item.id === id);
    if (city) {
      emit('select', city);
    }
  };
</script>

<template>
  <div
    class="seo-departure-picker"
    data-testid="seo-search-departure"
  >
    <Typo
      class="seo-departure-picker__label"
      :variant="TypoVariant.BodyMRegular"
      tag="span"
    >
      {{ departure.label }}
    </Typo>
    <!-- Выпадашка по макету выровнена по первой букве города, поэтому якорь — только город. -->
    <DropdownMenu
      :options="options"
      placement="bottom-start"
      width="160px"
      @change-visible="onVisible"
      @option-click="onSelect"
    >
      <template #reference>
        <button
          type="button"
          class="seo-departure-picker__trigger"
        >
          <Typo
            :variant="TypoVariant.BodyMRegular"
            tag="span"
          >
            {{ departure.city ? departure.city.name : '' }}
          </Typo>
          <Icon
            class="seo-departure-picker__chevron"
            :name="opened ? 'chevron-up' : 'chevron-down'"
            size="s"
          />
        </button>
      </template>
      <template #prepend>
        <Input
          ref="searchInput"
          v-model="query"
          :placeholder="departure.placeholder"
          size="m"
        />
      </template>
    </DropdownMenu>
  </div>
</template>

<style scoped lang="scss">
  .seo-departure-picker {
    align-items: center;
    align-self: center;
    display: flex;
    gap: var(--spacing-xxs);

    &__label {
      color: var(--color-text-inverse);
    }

    &__trigger {
      align-items: center;
      background: none;
      border: 0;
      color: var(--color-text-inverse);
      cursor: pointer;
      display: flex;
      gap: var(--spacing-xxs);
      padding: 0;
    }

    &__chevron {
      fill: var(--color-text-inverse);
    }
  }
</style>
